import { Loader } from '@mantine/core';
import { IconShieldOff } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useToggleBackupMutate } from '../../../../../hooks/CloudComputeHooks/useToggleBackupMutate';
import { useBackupSettingsQuery } from '../hooks/useBackupSettingsQuery';
import AutomatedBackupsCard from './AutomatedBackupsCard';
import BackupListCard from './BackupListCard';
import BackupPricingNotice from './BackupPricingNotice';

interface BackupTabPanelProps {
    instanceId: number;
    /** False with view-only access: settings and backups are shown without controls. */
    canManage: boolean;
}

export default function BackupTabPanel({ instanceId, canManage }: BackupTabPanelProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const { data: settings, isPending } = useBackupSettingsQuery(instanceId);
    const toggleMutation = useToggleBackupMutate();

    if (isPending && !settings) {
        return (
            <div className="w-full p-8 flex justify-center">
                <Loader size={24} color="var(--fg)" />
            </div>
        );
    }

    if (settings && settings.enabled === false) {
        return (
            <DialogBox
                label={t('backup.automatedTitle')}
                description={t('backup.automatedDescription')}
                classNames="p-6 gap-4 items-start"
            >
                <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2 text-fg/80">
                        <IconShieldOff size={20} />
                        <span className="text-base font-medium">{t('backup.disabledTitle')}</span>
                    </div>
                    <p className="text-sm text-fg/68 max-w-2xl">{t('backup.automatedBlurb')}</p>
                </div>
                {canManage && (
                    <Button
                        onClick={() => toggleMutation.mutate(instanceId)}
                        disabled={toggleMutation.isPending}
                    >
                        {toggleMutation.isPending
                            ? t('backup.enabling')
                            : t('backup.enableBackups')}
                    </Button>
                )}
            </DialogBox>
        );
    }

    return (
        <div className="w-full flex flex-col gap-4">
            <AutomatedBackupsCard
                instanceId={instanceId}
                settings={settings}
                canManage={canManage}
            />
            <BackupListCard instanceId={instanceId} settings={settings} canManage={canManage} />
            <BackupPricingNotice />
        </div>
    );
}
